import React, { Fragment, useState } from "react";
import CommonDialog from "../../common/components/CommonDialog";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import CommonCard from "../../common/components/CommonCard";
import CommonFormField from "../../common/components/CommonFormField";
import { Relations, validateEmail } from "../../common/const";
import CommonSelect from "../../common/components/CommonSelect";
import { Close, EmailOutlined } from "@mui/icons-material";
import { createParent } from "../../api/parentApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";

const AddParentAccount = ({
    addOpen,
    setAddOpen,
    getParentList
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        parentname: '',
        relation: '',
        email: '',
        phone: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'parentname':
                if (!value) return 'Parent Name is required';
                return '';
            case 'relation':
                if (!value) return 'Relation is required';
                return '';
            case 'email':
                if (!value) return 'Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                return '';
            case 'subject':
                if (!value) return 'Subject is required';
                return '';
            case 'phone':
                if (!value) return 'Phone Number is required';
                if (value.includes('+91') ? value.length < 12 : value.length < 11) return 'Please enter valid phone number';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await createParent({
                "first_name": formData.parentname,
                "email": formData.email,
                "phone_number": formData.phone,
                "student_id": user?.student_id,
                "relationship_type": formData.relation,
            });
            if (response.data) {
                showNotification('Parent account added successfully', 'success');
                setAddOpen(false);
                getParentList();
                dispatch(setLoading({
                    loading: false
                }));
                setFormData({
                    parentname: '',
                    relation: '',
                    email: '',
                    phone: ''
                });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <CommonDialog
            open={addOpen}
            size="xs"
            title={
                <Fragment>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Add Parent Account</Typography>
                        <Close
                            sx={{
                                color: theme.palette.background.dark, fontSize: 20,
                                float: 'right',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                setAddOpen(false);
                                setFormData({
                                    parentname: '',
                                    relation: '',
                                    email: '',
                                    phone: ''
                                });
                            }}
                        />
                    </Box>
                    <Box width="80%" mb={0.5}>
                        <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Invite a parent or guardian to view your college preparation progress. They will receive an email with login credentials.</Typography>
                    </Box>
                    <Divider />
                </Fragment>
            }
            content={
                <Grid container spacing={1.5} mt={1} mb={2}>
                    {loading && (
                        <LoadingSpinner />
                    )}
                    <Grid item xs={12} md={12}>
                        <CommonFormField
                            label="Parent Name *"
                            type="email"
                            name="parentname"
                            value={formData.parentname}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            placeholder="e.g., John Smith"
                            error={errors.parentname}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <CommonSelect
                            label="Relation *"
                            name="relation"
                            value={formData.relation}
                            onChange={handleInputChange}
                            placeholder="Select Relation"
                            options={Relations}
                            error={errors.relation}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <CommonFormField
                            label="Email *"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            placeholder="parent@gmail.com"
                            error={errors.email}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <CommonFormField
                            label="Phone Number *"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    phone: e?.phone,
                                }));
                                if (errors['phone']) {
                                    const error = validateField('phone', e?.phone);
                                    if (!error) {
                                        setErrors(prev => ({ ...prev, phone: undefined }));
                                    } else {
                                        setErrors(prev => ({ ...prev, phone: error }));
                                    }
                                }
                            }}
                            onBlur={handleBlur}
                            placeholder="(555) 123-4567"
                            error={errors.phone}
                            isPhone={true}
                        />
                    </Grid>
                    <Grid item md={12}>
                        <CommonCard
                            borderRadius={1}
                            border={`1px solid ${theme.palette.cardbackground.main}`}
                            background={theme.palette.cardbackground.secondary}
                            padding="10px"
                            paddingBottom="0px"
                            marginTop="8px"
                            content={
                                <Fragment>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <EmailOutlined sx={{ color: '#155DFC' }} />
                                        <Typography fontWeight={600}>After you submit:</Typography>
                                    </Box>
                                    <ul
                                        style={{ fontSize: '12px', margin: 0, color: theme.palette.secondary.main }}
                                    >
                                        <li>Parent will receive an invitation email</li>
                                        <li>Email contains login link and credentials</li>
                                        <li>Parent can view your progress in read-only mode</li>
                                        <li>You can remove access at any time</li>
                                    </ul>
                                </Fragment>
                            }
                        />
                    </Grid>
                </Grid>
            }
            actionButtons={(
                <CommonButton
                    label="Send Invitation"
                    size="small"
                    background={theme.palette.secondary.main}
                    color={theme.palette.background.main}
                    fontWeight={700}
                    marginBottom="10px"
                    handleClick={() => {
                        handleSubmit();
                    }}
                />
            )}
        />
    )
}

export default AddParentAccount;